import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ISnackBarProps,
    NCoreUIKitSnackBar,
    SnackBar,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DocsTable,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "SnackBar";

const USAGE_CODE = `import {
    NCoreUIKitSnackBar
} from "ncore-ui-kit";

NCoreUIKitSnackBar.open({
    subTitle: "The October report was shared with 3 people.",
    action: {
        onPress: () => openReport(),
        title: "View"
    },
    title: "Report sent",
    type: "success"
});`;

const ACTION_CODE = `NCoreUIKitSnackBar.open({
    action: {
        onPress: ({ closeAnimation }) => closeAnimation({
            onClosed: () => retryUpload()
        }),
        title: "Retry"
    },
    isCloseOnPressActionButton: false,
    title: "Upload failed",
    type: "danger"
});`;

const COMPONENT_CODE = `const [isOpen, setIsOpen] = useState(false);

{isOpen ? <SnackBar
    onClosed={() => setIsOpen(false)}
    title="Report sent"
    id="report-sent"
    type="success"
/> : null}`;

type SnackBarTypeValue = NonNullable<ISnackBarProps["type"]>;

type SnackBarAlignValue = NonNullable<ISnackBarProps["align"]>;

type SnackBarClosingSample = {
    props: Pick<ISnackBarProps, "autoCloseDelay" | "isAutoClose" | "isCloseOnPress" | "isCloseOnSwipe">;
    messageKey: string;
    labelKey: string;
};

const TYPES: Array<SnackBarTypeValue> = [
    "primary",
    "neutral",
    "success",
    "info",
    "warning",
    "danger"
];

const TYPE_MESSAGES: Record<SnackBarTypeValue, string> = {
    neutral: "snackBarPage.messageNeutral",
    primary: "snackBarPage.messagePrimary",
    success: "snackBarPage.messageSuccess",
    warning: "snackBarPage.messageWarning",
    danger: "snackBarPage.messageDanger",
    info: "snackBarPage.messageInfo"
};

const ALIGNS: Array<SnackBarAlignValue> = [
    "start",
    "center",
    "end"
];

const CLOSING_SAMPLES: Array<SnackBarClosingSample> = [
    {
        messageKey: "snackBarPage.shortDelayMessage",
        labelKey: "snackBarPage.shortDelay",
        props: {
            autoCloseDelay: 2000
        }
    },
    {
        messageKey: "snackBarPage.stayOpenMessage",
        labelKey: "snackBarPage.stayOpen",
        props: {
            isAutoClose: false
        }
    },
    {
        messageKey: "snackBarPage.touchOffMessage",
        labelKey: "snackBarPage.touchOff",
        props: {
            isCloseOnPress: false,
            isCloseOnSwipe: false,
            autoCloseDelay: 4000
        }
    }
];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("snackBarPage.typesTitle"),
        id: "types"
    },
    {
        title: t("snackBarPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("snackBarPage.alignTitle"),
        id: "position"
    },
    {
        title: t("snackBarPage.closingTitle"),
        id: "closing"
    },
    {
        title: t("snackBarPage.imperativeTitle"),
        id: "imperative"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SnackBarPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isReportViewed,
        setIsReportViewed
    ] = useState(false);

    const [
        isComponentOpen,
        setIsComponentOpen
    ] = useState(false);

    const handleReportView = () => setIsReportViewed(true);

    const handleReportOpen = () => {
        setIsReportViewed(false);

        NCoreUIKitSnackBar.open({
            subTitle: t("snackBarPage.reportBody"),
            title: t("snackBarPage.reportTitle"),
            action: {
                title: t("snackBarPage.view"),
                onPress: handleReportView
            },
            type: "success"
        });
    };

    const handleTypeOpen = (type: SnackBarTypeValue) => {
        NCoreUIKitSnackBar.open({
            title: t(TYPE_MESSAGES[type]),
            type
        });
    };

    const handleActionOpen = () => {
        NCoreUIKitSnackBar.open({
            action: {
                title: t("snackBarPage.undo"),
                onPress: () => undefined
            },
            title: t("snackBarPage.archived")
        });
    };

    const handleCloseOnlyOpen = () => {
        NCoreUIKitSnackBar.open({
            title: t("snackBarPage.archived"),
            isShowAction: false,
            isShowClose: true
        });
    };

    const handleBareOpen = () => {
        NCoreUIKitSnackBar.open({
            title: t("snackBarPage.archived"),
            isShowAction: false
        });
    };

    const handleAlignOpen = (align: SnackBarAlignValue) => {
        NCoreUIKitSnackBar.open({
            title: t("snackBarPage.alignMessage", [
                align
            ]),
            align
        });
    };

    const handleFullWidthOpen = () => {
        NCoreUIKitSnackBar.open({
            title: t("snackBarPage.fullWidthMessage"),
            isFullWidth: true
        });
    };

    const handleClosingOpen = (sample: SnackBarClosingSample) => {
        NCoreUIKitSnackBar.open({
            ...sample.props,
            title: t(sample.messageKey)
        });
    };

    const handleCodeOpen = () => {
        NCoreUIKitSnackBar.open({
            title: t("snackBarPage.codeMessage"),
            type: "info"
        });
    };

    return <Article>
        <ComponentHeader
            lead={t("snackBarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Callout
                title={t("snackBarPage.deprecatedTitle")}
            >
                {t("snackBarPage.deprecatedText")}
            </Callout>
            <DocsTable
                rows={[
                    [
                        "<SnackBar />",
                        "<Toast side=\"top\" stackMode=\"list\" isCompact={false} />"
                    ],
                    [
                        "NCoreUIKitSnackBar.open / close",
                        "NCoreUIKitToast.open / close"
                    ],
                    [
                        "topOffset",
                        "edgeOffset"
                    ],
                    [
                        "isFullWidth",
                        "spreadBehaviour=\"stretch\""
                    ],
                    [
                        "isInlineSafeArea",
                        t("snackBarPage.mappingRemoved")
                    ]
                ]}
                headers={[
                    t("snackBarPage.mappingOld"),
                    t("snackBarPage.mappingNew")
                ]}
            />
            <Paragraph>
                {t("snackBarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("snackBarPage.overviewCaption")}
            >
                <Button
                    title={t("snackBarPage.sendReport")}
                    onPress={handleReportOpen}
                    variant="outline"
                    type="neutral"
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t(isReportViewed ? "snackBarPage.statusViewed" : "snackBarPage.statusIdle")}
                </Text>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("snackBarPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("snackBarPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <Button
                        onPress={() => handleTypeOpen(type)}
                        variant="outline"
                        type="neutral"
                        title={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("snackBarPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("snackBarPage.actionsText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("snackBarPage.withAction")}
                    onPress={handleActionOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("snackBarPage.closeOnly")}
                    onPress={handleCloseOnlyOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("snackBarPage.noButtons")}
                    onPress={handleBareOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={ACTION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("snackBarPage.alignTitle")}
            id="position"
        >
            <Paragraph>
                {t("snackBarPage.alignText")}
            </Paragraph>
            <Preview>
                {
                    ALIGNS.map((align) => <Button
                        onPress={() => handleAlignOpen(align)}
                        variant="outline"
                        type="neutral"
                        title={align}
                        key={align}
                    />)
                }
                <Button
                    onPress={handleFullWidthOpen}
                    title="isFullWidth"
                    variant="outline"
                    type="neutral"
                />
            </Preview>
        </Section>
        <Section
            title={t("snackBarPage.closingTitle")}
            id="closing"
        >
            <Paragraph>
                {t("snackBarPage.closingText")}
            </Paragraph>
            <Preview>
                {
                    CLOSING_SAMPLES.map((sample) => <Button
                        onPress={() => handleClosingOpen(sample)}
                        title={t(sample.labelKey)}
                        key={sample.labelKey}
                        variant="outline"
                        type="neutral"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("snackBarPage.imperativeTitle")}
            id="imperative"
        >
            <Paragraph>
                {t("snackBarPage.imperativeText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("snackBarPage.openFromCode")}
                    onPress={handleCodeOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("snackBarPage.openAsComponent")}
                    onPress={() => setIsComponentOpen(true)}
                    variant="outline"
                    type="neutral"
                />
                {
                    isComponentOpen ?
                        <SnackBar
                            title={t("snackBarPage.componentMessage")}
                            onClosed={() => setIsComponentOpen(false)}
                            id="docs-snack-bar"
                            type="info"
                        />
                    :
                        null
                }
            </Preview>
            <CodeBlock
                code={COMPONENT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("snackBarPage.a11yLive"),
                    t("snackBarPage.a11yProvider"),
                    t("snackBarPage.a11yKeyboard"),
                    t("snackBarPage.a11yTimer"),
                    t("snackBarPage.a11yFocus"),
                    t("snackBarPage.a11yIcon"),
                    t("snackBarPage.a11yMotion"),
                    t("snackBarPage.a11yDeprecated")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("snackBarPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("snackBarPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("snackBarPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("snackBarPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("snackBarPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("snackBarPage.propAlign"),
                        type: "\"start\" | \"center\" | \"end\"",
                        defaultValue: "\"center\"",
                        name: "align"
                    },
                    {
                        description: t("snackBarPage.propIsFullWidth"),
                        defaultValue: "false",
                        name: "isFullWidth",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"free\"",
                        name: "spreadBehaviour"
                    },
                    {
                        type: "{ title, onPress({ closeAnimation }), icon, style }",
                        description: t("snackBarPage.propAction"),
                        name: "action"
                    },
                    {
                        description: t("snackBarPage.propIsShowAction"),
                        defaultValue: "true",
                        name: "isShowAction",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propIsShowClose"),
                        name: "isShowClose",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propIsCloseOnPressActionButton"),
                        name: "isCloseOnPressActionButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propIsAutoClose"),
                        defaultValue: "true",
                        name: "isAutoClose",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propAutoCloseDelay"),
                        name: "autoCloseDelay",
                        type: "number"
                    },
                    {
                        description: t("snackBarPage.propIsCloseOnPress"),
                        name: "isCloseOnPress",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propIsCloseOnSwipe"),
                        name: "isCloseOnSwipe",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propOnPress"),
                        type: "({ id }) => void",
                        name: "onPress"
                    },
                    {
                        description: t("snackBarPage.propOnClosed"),
                        type: "({ id }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("snackBarPage.propOpenAnimationDelay"),
                        name: "openAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("snackBarPage.propCloseAnimationDelay"),
                        name: "closeAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("snackBarPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("snackBarPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("snackBarPage.propIsLiveRegionEnabled"),
                        name: "isLiveRegionEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("snackBarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("snackBarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("snackBarPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SnackBarPage;
